<template>
    <!--
        範本分享代碼（studio-api docs/plans/TemplateShare.spec.md）。
        ⚠ 跟旁邊的「查看範本代碼」是兩種代碼：那一個是領取者共用原題，這一個是深拷貝一份自己的。
        ⚠ 能不能分享由呼叫端先打 GET shareCode 決定（404 就不要顯示開啟的按鈕），這裡不重判。
    -->
    <v-dialog :value="value" width="560" @input="$emit('input', $event)">
        <v-card>
            <v-card-title>{{ $t('unit.share.title') }}</v-card-title>
            <v-card-text>
                <div class="sc-note">{{ $t('unit.share.hint') }}</div>

                <div v-if="shareCode" class="sc-code-row">
                    <span class="sc-code">{{ shareCode }}</span>
                    <v-btn small depressed color="primary" class="normal-case" @click="copy">
                        <v-icon small left>mdi-content-copy</v-icon>{{ $t('unit.share.copy') }}
                    </v-btn>
                </div>

                <div v-if="errorMessage" class="sc-note sc-note--warn">{{ errorMessage }}</div>
            </v-card-text>
            <v-card-actions>
                <v-btn v-if="!shareCode" depressed color="primary" class="normal-case"
                    :loading="isBusy" @click="regenerate">
                    {{ $t('unit.share.generate') }}
                </v-btn>
                <template v-else>
                    <v-btn text color="grey darken-3" class="normal-case"
                        :disabled="isBusy" @click="confirmAction = 'regenerate'">
                        {{ $t('unit.share.regenerate') }}
                    </v-btn>
                    <v-btn text color="red darken-1" class="normal-case"
                        :disabled="isBusy" @click="confirmAction = 'disable'">
                        {{ $t('unit.share.disable') }}
                    </v-btn>
                </template>
                <v-spacer></v-spacer>
                <v-btn text class="normal-case" @click="$emit('input', false)">
                    {{ $t('unit.share.close') }}
                </v-btn>
            </v-card-actions>
        </v-card>

        <!-- 換一組與停用都會讓舊代碼立刻失效，已經傳出去的代碼就不能用了，所以要確認 -->
        <v-dialog :value="confirmAction !== null" persistent width="480">
            <v-card>
                <v-card-title class="text-h6">
                    {{ confirmAction === 'disable'
                        ? $t('unit.share.disable_confirm_title')
                        : $t('unit.share.regenerate_confirm_title') }}
                </v-card-title>
                <v-card-text>{{ $t('unit.share.confirm_message') }}</v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text class="normal-case" @click="confirmAction = null">
                        {{ $t('button.cancel') }}
                    </v-btn>
                    <v-btn depressed :color="confirmAction === 'disable' ? 'red darken-1' : 'primary'"
                        class="white--text normal-case" @click="runConfirmed">
                        {{ confirmAction === 'disable' ? $t('unit.share.disable') : $t('unit.share.regenerate') }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-dialog>
</template>

<script>
import { templateUnitServices } from '@/services/templateUnit.js';

/**
 * 用法：`<TemplateShareDialog v-model="open" :homework-id="id" :share-code.sync="code">`
 * `shareCode` 由呼叫端先用 GET shareCode 載入；產生、換一組、停用之後以 `update:shareCode` 回寫。
 */
export default {
    name: 'TemplateShareDialog',
    props: {
        value: { type: Boolean, default: false },
        homeworkId: { type: [String, Number], required: true },
        shareCode: { type: String, default: null },
    },
    data() {
        return {
            isBusy: false,
            errorMessage: '',
            /// null / 'regenerate' / 'disable'
            confirmAction: null,
        };
    },
    watch: {
        value(open) {
            if (open) {
                this.errorMessage = '';
            }
        },
    },
    methods: {
        async regenerate() {
            this.isBusy = true;
            this.errorMessage = '';
            try {
                const res = await templateUnitServices.regenerateShareCode(this.homeworkId);
                this.$emit('update:shareCode', res.data.share_code);
            } catch (err) {
                this.errorMessage = err.status === 409
                    ? this.$t('unit.share.has_sub_map')
                    : this.$t('unit.share.action_failed');
            } finally {
                this.isBusy = false;
            }
        },
        async disable() {
            this.isBusy = true;
            this.errorMessage = '';
            try {
                await templateUnitServices.disableShareCode(this.homeworkId);
                this.$emit('update:shareCode', null);
            } catch (err) {
                this.errorMessage = this.$t('unit.share.action_failed');
            } finally {
                this.isBusy = false;
            }
        },
        async runConfirmed() {
            const action = this.confirmAction;
            this.confirmAction = null;
            if (action === 'disable') {
                await this.disable();
            } else {
                await this.regenerate();
            }
        },
        async copy() {
            try {
                await navigator.clipboard.writeText(this.shareCode);
                this.$toastr.Add({
                    msg: this.$t('unit.share.copied'),
                    clickClose: true,
                    timeout: 3000,
                    position: "toast-top-right",
                    classNames: ["animated", "bounceInRight"],
                });
            } catch (err) {
                /// 剪貼簿被瀏覽器擋掉時代碼仍在畫面上，老師可以自己選取複製
                this.errorMessage = this.$t('unit.share.copy_failed');
            }
        },
    },
};
</script>

<style scoped>
.sc-note {
    font-size: 14px;
    color: #616161;
    line-height: 1.6;
}

.sc-note--warn {
    color: #e65100;
    margin-top: 12px;
}

.sc-code-row {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 16px;
}

.sc-code {
    font-family: ui-monospace, Menlo, Consolas, monospace;
    font-size: 20px;
    letter-spacing: 1px;
    color: #212121;
    user-select: all;
}
</style>
